import React, { useRef, useState } from 'react';
import classnames from 'classnames';
import { IConnectionDetailsDTO, IConnectionGroupDTO } from '../../../../../../api/connections.dao';
import { IMeetingLinkDTO } from '../../../../../../api/meeting-links.dao';
import ArrowUpSVG from '../../../../../../images/svg/icons8-arrow-up';
import ChevronSVG from '../../../../../../images/svg/icons8-expand-arrow';
import Text from '../../../../../global/text/text';
import { Popover } from '../../../../../global/popover/popover';
import TextWithCopyBtn from '../../../shared/connections-list/_text-with-copy-btn/text-with-copy-btn';
import { MeetingLinksViewModel } from './meeting-links.viewmodel';
import { MEETING_LINK_ROLE_TEXT } from './meeting-links.texts';
import { MEETING_LINK_TEXT } from '../../../_meetings-list/_meeting-form/meeting-form.texts';
import OptionsButton from '../../../../../global/options-button/options-button';
import { FormattedMessage, useIntl } from 'react-intl';
import { DELETE_TEXT, EDIT_TEXT, MORE_BUTTON_TEXT } from '../../../../../../constants/common-labels.constants';
import { MeetingLinkInheritanceIds } from './manage-meeting-link-modal/manage-meeting-link-modal.viewmodel';

export type MeetingLinkItemProps = {
    link: IMeetingLinkDTO;
    vm: MeetingLinksViewModel;
    connections: IConnectionDetailsDTO[];
    connectionGroups: IConnectionGroupDTO[];
    inheritanceConnections?: (IConnectionDetailsDTO | IConnectionGroupDTO)[];
    inheritanceIds?: MeetingLinkInheritanceIds;
    canManage: boolean;
    isEditing?: boolean;
    onEdit: (link: IMeetingLinkDTO) => void;
};

export const MeetingLinkItem = ({
    link,
    vm,
    connections,
    connectionGroups,
    inheritanceConnections = [],
    inheritanceIds,
    canManage,
    isEditing = false,
    onEdit,
}: MeetingLinkItemProps): JSX.Element => {
    const intl = useIntl();
    const [isPopoverOpen, setIsPopoverOpen] = useState(false);
    const listItemRef = useRef<HTMLDivElement>(null);

    const handleOpenPopover = (event: React.MouseEvent): void => {
        event.stopPropagation();
        setIsPopoverOpen(!isPopoverOpen);
    };

    const optionsButton = [
        {
            label: intl.formatMessage(EDIT_TEXT),
            action: () => {
                setIsPopoverOpen(false);
                onEdit(link);
            },
        },
        {
            label: intl.formatMessage(DELETE_TEXT),
            // eslint-disable-next-line no-empty-function
            action: () => { },
            danger: true
        },
    ];

    const connectionDisplayName = vm.getConnectionDisplayName(
        link,
        connections,
        connectionGroups,
        inheritanceConnections,
        inheritanceIds
    );

    return (
        <div className='meeting-links__row' data-testid={`meeting-link-row-${link.meetingLinkId}`}>
            <div
                ref={listItemRef}
                className={classnames('meeting-links__item', {
                    'meeting-links__item--active': isPopoverOpen,
                    'meeting-links__item--editing': isEditing && !isPopoverOpen,
                })}
                onClick={handleOpenPopover}
            >
                <Text tag='p' message={link.label} withEllipsis />
                <div className='meeting-links__chevron'>
                    <ChevronSVG />
                </div>
            </div>
            <div
                className={classnames('meeting-links__connection', {
                    'meeting-links__connection--assigned': Boolean(connectionDisplayName),
                    'meeting-links__connection--inherited': Boolean(connectionDisplayName) && link.connectionInherited,
                })}
            >
                {connectionDisplayName && link.connectionInherited && <ArrowUpSVG />}
                <span>{connectionDisplayName}</span>
                {connectionDisplayName &&
                    <div className='meeting-links__chevron'>
                        <ChevronSVG />
                    </div>
                }
            </div>
            <div className='meeting-links__popover-anchor'>
                <Popover
                    isOpen={isPopoverOpen}
                    buttonRef={listItemRef}
                    onClickOutside={() => setIsPopoverOpen(false)}
                    padding={0}
                    align='start'
                >
                    <div className='meeting-links__popover' onClick={event => event.stopPropagation()}>
                        {link.role && <div className='meeting-links__popover__field'>{MEETING_LINK_ROLE_TEXT.defaultMessage}:<br />
                            <Text className='text-with-copy__label' message={link.role} withEllipsis />
                        </div>}
                        {link.url && <div className='meeting-links__popover__field'>{MEETING_LINK_TEXT.defaultMessage}:<br />
                            <TextWithCopyBtn message={link.url} handleCopyClick={vm.trackCopyMeetingLink} />
                        </div>}
                    </div>
                    {canManage &&
                        <OptionsButton
                            className='small-button'
                            label={<FormattedMessage {...MORE_BUTTON_TEXT} />}
                            options={optionsButton}
                            position='top'
                            align='start'
                        />
                    }
                </Popover>
            </div>
        </div>
    );
};
